/**
 * Validatori condivisi per i dati anagrafici (atleti e genitori).
 * Ogni funzione restituisce il messaggio d'errore da mostrare, oppure null se il valore è valido.
 */

const FISCAL_CODE_PATTERN = /^[A-Z]{6}[0-9LMNPQRSTUV]{2}[ABCDEHLMPRST][0-9LMNPQRSTUV]{2}[A-Z][0-9LMNPQRSTUV]{3}[A-Z]$/;
const FISCAL_CODE_MONTHS = 'ABCDEHLMPRST';
const FISCAL_CODE_OMOCODIA = 'LMNPQRSTUV';
const FISCAL_CODE_ODD_VALUES = [1, 0, 5, 7, 9, 13, 15, 17, 19, 21, 2, 4, 18, 20, 11, 3, 6, 8, 12, 14, 16, 10, 22, 25, 24, 23];
const NAME_PATTERN = /^[\p{L}][\p{L}' .-]*$/u;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function toIsoDate(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export const todayIsoDate = () => toIsoDate(new Date());
export const minBornIsoDate = () => toIsoDate(new Date(new Date().getFullYear() - 100, 0, 1));

/** Maiuscolo, solo lettere e cifre, massimo 16 caratteri. */
export function normalizeFiscalCode(value: string | undefined | null): string {
    return (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16);
}

/** Comprime gli spazi multipli e rimuove quelli iniziali/finali. */
export function normalizeSpaces(value: string | undefined | null): string {
    return (value || '').replace(/\s+/g, ' ').trim();
}

export function validateName(value: string | undefined | null, label: string): string | null {
    const trimmed = (value || '').trim();
    if (!trimmed) return `${label} è obbligatorio.`;
    if (trimmed.length < 2) return `${label} deve avere almeno 2 caratteri.`;
    if (!NAME_PATTERN.test(trimmed)) return `${label} può contenere solo lettere, spazi, apostrofi e trattini.`;
    return null;
}

export function validateRequiredText(value: string | undefined | null, message: string, minLength = 2): string | null {
    const trimmed = (value || '').trim();
    if (!trimmed) return message;
    if (trimmed.length < minLength) return `Inserisci almeno ${minLength} caratteri.`;
    return null;
}

export function validateBornDate(value: string | undefined | null): string | null {
    if (!value) return 'Inserisci la data di nascita.';
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'Inserisci una data valida.';
    if (value > todayIsoDate()) return 'La data di nascita non può essere nel futuro.';
    if (value < minBornIsoDate()) return 'Controlla l\'anno di nascita.';
    return null;
}

export function validateFiscalCode(value: string | undefined | null): string | null {
    const code = value || '';
    if (!code) return 'Inserisci il codice fiscale.';
    if (code.length !== 16) return 'Il codice fiscale deve avere 16 caratteri.';
    if (!FISCAL_CODE_PATTERN.test(code)) return 'Il formato del codice fiscale non è valido.';
    if (!hasValidFiscalCodeCheckChar(code)) return 'Il codice fiscale non è corretto: controlla di averlo digitato bene.';
    return null;
}

export function validateEmail(value: string | undefined | null): string | null {
    const trimmed = (value || '').trim();
    if (!trimmed) return 'Inserisci l\'indirizzo email.';
    if (!EMAIL_PATTERN.test(trimmed)) return 'Inserisci un indirizzo email valido, ad esempio nome@esempio.it.';
    return null;
}

export function validatePhone(value: string | undefined | null): string | null {
    const digits = (value || '').replace(/\D/g, '');
    if (!digits) return 'Inserisci il numero di telefono.';
    if (digits.length < 7 || digits.length > 15) return 'Inserisci un numero di telefono valido.';
    return null;
}

export function validateZip(value: string | undefined | null): string | null {
    const trimmed = (value || '').trim();
    if (!trimmed) return 'Inserisci il CAP.';
    if (!/^\d{5}$/.test(trimmed)) return 'Il CAP deve avere 5 cifre.';
    return null;
}

/**
 * Controllo "morbido": true se codice fiscale e data di nascita sono validi ma la data codificata
 * nel codice fiscale non coincide con quella inserita.
 */
export function fiscalCodeBirthDateMismatch(fiscalCode: string | undefined | null, bornDate: string | undefined | null): boolean {
    if (validateFiscalCode(fiscalCode) || validateBornDate(bornDate)) {
        return false;
    }
    const code = fiscalCode as string;
    const digits = (chars: string) => Number(chars.replace(/[LMNPQRSTUV]/g, c => String(FISCAL_CODE_OMOCODIA.indexOf(c))));
    const encodedMonth = FISCAL_CODE_MONTHS.indexOf(code[8]) + 1;
    const encodedDay = digits(code.slice(9, 11));
    const [year, month, day] = (bornDate as string).split('-').map(Number);
    return digits(code.slice(6, 8)) !== year % 100
        || encodedMonth !== month
        || (encodedDay > 40 ? encodedDay - 40 : encodedDay) !== day;
}

function hasValidFiscalCodeCheckChar(code: string): boolean {
    const valueOf = (char: string) => /\d/.test(char) ? Number(char) : char.charCodeAt(0) - 65;
    let sum = 0;
    for (let i = 0; i < 15; i++) {
        const value = valueOf(code[i]);
        sum += i % 2 === 0 ? FISCAL_CODE_ODD_VALUES[value] : value;
    }
    return String.fromCharCode(65 + (sum % 26)) === code[15];
}
